import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { resolveScan, type ScanMatch } from './api';

/**
 * Where a resolved code takes you.
 *
 * One function rather than one per caller: the scan screen, the camera, the
 * wired scanner and the address `/r/<code>` all end at the same place, and the
 * moment they stop agreeing is the moment a label starts meaning two things.
 */
export function useOpenScanned(): (match: ScanMatch) => void {
  const navigate = useNavigate();

  return useCallback(
    (match: ScanMatch) => {
      switch (match.kind) {
        case 'component':
          navigate(`/components/${match.id}`);
          return;
        case 'location':
          navigate(`/locations/${match.id}`);
          return;
        case 'project':
          navigate(`/projects/${match.id}`);
          return;
        case 'purchase':
          navigate(`/purchases/${match.id}`);
          return;
        case 'spec_version':
          // The code on a kit box names a version; the screen shows the
          // specification it belongs to, with that version open.
          navigate(`/specs?version=${encodeURIComponent(match.id)}`);
          return;
        case 'stocktake':
          navigate(`/stocktakes/${match.id}`);
          return;
        case 'serial':
          // A unit lives inside its component's register, but the query opens
          // this exact row rather than merely taking the person to the part.
          if (match.opens_component_id) {
            navigate(`/components/${match.opens_component_id}?serial=${encodeURIComponent(match.id)}`);
            return;
          }
          navigate('/stock');
          return;
        case 'package':
          if (match.opens_component_id) {
            navigate(`/components/${match.opens_component_id}?package=${encodeURIComponent(match.id)}`);
            return;
          }
          navigate('/stock');
          return;
        default:
          navigate('/stock');
      }
    },
    [navigate],
  );
}

/**
 * A raw reading from a scanner, with nowhere in particular to go.
 *
 * Everything the server understands arrives here as one string — our own short
 * code, the whole `https://host/r/…` URL a 2-D imager reads off the QR, or a
 * manufacturer's barcode off a reel — so it is `/resolve` that decides what it
 * was. A code that resolves to nothing lands on the scanning screen, which is
 * the one place that can offer to bind it to a part.
 */
export function useResolveAndOpen(): (value: string) => void {
  const navigate = useNavigate();
  const open = useOpenScanned();

  return useCallback(
    (value: string) => {
      void resolveScan(value)
        .then((found) => {
          if (found.resolved && found.match) open(found.match);
          else navigate('/scan', { state: { unresolved: value } });
        })
        .catch(() => navigate('/scan', { state: { unresolved: value } }));
    },
    [navigate, open],
  );
}
